import React, { useState, useEffect } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import apiClient from "../utils/apiClient";
import { NODE_BACKEND_URL } from "../utils/constant";
import {
  fetchVideoAnalysis,
  getSelectedSport,
  processTextFormatting,
  successMessage,
  getUser,
  getAuthToken
} from "../utils/helpers";
import VideoAnalyticsModal from "./VideoAnalyticsModal";
import { Video, BookOpen, PlayCircle, ChevronLeft, Folder, FolderOpen, RefreshCw, ExternalLink, Calendar, Trash2, Plus, X, BarChart3, Image } from "lucide-react";
import { Tabs, TabsList, TabsTrigger, TabsContent, ConfirmDialog, Pagination, Separator } from "./design-system";
import FolderDropdown from "./FolderDropdown";
import VideoPlayer from "./VideoPlayer";
import GameStatsTab from "./GameStatsTabs";
import BasketballRadarChart from "./BasketballRadarChart";
import VideoAnalyticsTable from './VideoAnalyticsTable';
import useVideoAnalysisNotifications from '../hooks/useVideoAnalysisNotifications';
// MLB Profile Service - Local cache + MLB Stats API fallback
import { getMLBPlayerProfile, getMLBPlayerStats, processMLBProfile, fetchMLBPlayerProfile, fetchMLBPlayerStats } from '../lib/mlbPlayerProfileService';
// Biometric/Health Integration
import { BiometricBadgesRow } from './BiometricBadges';

const TeamPlayerProfile = () => {
  const { playerId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [player, setPlayer] = useState(null);
  const [playerStats, setPlayerStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [videoModal, setVideoModal] = useState(false);
  const [error, setError] = useState(null);
  const [sportType, setSportType] = useState("");
  const [existingVideos, setExistingVideos] = useState([]);
  const [selectedVideo, setSelectedVideo] = useState(null);

  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalTeamsStats, setTotalTeamsStats] = useState(0);
  const [activeStatsTab, setActiveStatsTab] = useState("batting");
  const [activeMainTab, setActiveMainTab] = useState("playerStats");
  const [tableStats, setTableStats] = useState([]);
  const [seasonStats, setSeasonStats] = useState([]);

  // Folder state
  const [selectedFolderId, setSelectedFolderId] = useState(null);
  const [dropdownFolderId, setDropdownFolderId] = useState(null);
  const [allFolders, setAllFolders] = useState([]);
  const [isFolderLoading, setIsFolderLoading] = useState(false);
  const [showFoldersTable, setShowFoldersTable] = useState(false);

  // Pagination state
  const [currentVideoPage, setCurrentVideoPage] = useState(1);
  const [videoPageSize, setVideoPageSize] = useState(10);
  const [totalVideoRecords, setTotalVideoRecords] = useState(0);

  // Video analysis notifications - polls for status changes and shows notifications
  useVideoAnalysisNotifications({
    enabled: true,
    pollInterval: 30000,
    folderId: selectedFolderId,
    onStatusChange: () => {
      fetchExistingVideos(currentVideoPage, videoPageSize);
    },
  });

  useEffect(() => {
    if (playerId) {
      const currentSport = getSelectedSport();
      setSportType(currentSport);
      fetchData(currentSport);
      fetchTeamStatsData(1, pageSize, "B");
    }
  }, [playerId]);

  useEffect(() => {
    fetchExistingVideos();
    fetchAllFolders();
    
    // Check if URL has folder parameter
    const searchParams = new URLSearchParams(location.search);
    const folderId = searchParams.get('folder');
    if (folderId) {
      setSelectedFolderId(folderId);
    }
  }, [playerId, activeMainTab, location.search]);
  
  // Function to fetch all folders
  const fetchAllFolders = async () => {
    setIsFolderLoading(true);
    try {
      // Use axios directly with NODE_BACKEND_URL (not apiClient which uses gateway)
      const response = await axios.get(`${NODE_BACKEND_URL}/api/get-folders?user_id=${getUser()?.id}`, {
        headers: { Authorization: getAuthToken() }
      });
      setAllFolders(response.data?.data || []);
    } catch (error) {
      console.error('Error fetching folders:', error);
      setAllFolders([]);
    } finally {
      setIsFolderLoading(false);
    }
  };

  const [isTableLoading, setIsTableLoading] = useState(false);
  
  const fetchExistingVideos = async (page = 1, size = 10) => {
    setIsTableLoading(true);
    try {
      const videos = await fetchVideoAnalysis(playerId, page, size, selectedFolderId);
      // console.log('videos',videos)
      setExistingVideos(videos?.videos);
      setTotalVideoRecords(videos?.total);
    } catch (error) {
      console.error('Error fetching existing videos:', error);
    } finally {
      setIsTableLoading(false);
    }
  };
  
  // Handle folder click
  const handleFolderClick = (folderId, folderName) => {
    setSelectedFolderId(folderId);
    setDropdownFolderId(folderId)
    // Update URL with folder ID
    navigate(`/player/${playerId}?folder=${folderId}`);
    fetchExistingVideos(currentVideoPage, videoPageSize);
  };
  
  // Handle folder deletion
  const handleDeleteFolder = async (folderId) => {
    try {
      // Use axios directly with NODE_BACKEND_URL (not apiClient which uses gateway)
      const response = await axios.post(`${NODE_BACKEND_URL}/api/delete-folder`, {
        id: folderId,
        user_id: getUser()?.id
      }, {
        headers: { Authorization: getAuthToken() }
      });

      if (response.data?.success) {
        successMessage('Folder deleted successfully');
        // Remove folder from state and dropdown state
        setAllFolders(prev => prev.filter(folder => folder.id !== folderId));
        
        // Update the dropdown folder selection if needed
        if (dropdownFolderId === folderId) {
          setDropdownFolderId(null);
        }

        // If the current selected folder is deleted, reset selection
        if (selectedFolderId === folderId) {
          setSelectedFolderId(null);
          navigate(`/player/${playerId}`); // Remove folder from URL
        }
        
        // Refresh the folders to ensure the dropdown is updated
        fetchAllFolders();
      }
    } catch (error) {
      console.error('Error deleting folder:', error);
    }
  };

  const handleVideoPageChange = (page, size) => {
    setCurrentVideoPage(page);
    setVideoPageSize(size);
    fetchExistingVideos(page, size)
  };

  const handlePageChange = (page, size) => {
    setCurrentPage(page);
    setPageSize(size);

    // Get the current stats type based on active tab
    const statsType = activeStatsTab === "batting" ? "B" : "P";

    // Use a local loading state instead of the global one
    fetchTeamStatsData(page, size, statsType);
  };

  // Add a new state for table loading
  const [tableLoading, setTableLoading] = useState(false);

  const fetchTeamStatsData = async (page = 1, size = 10, stats_type = "B") => {
    // Use separate loading state for table data
    setTableLoading(true);
    setError(null);

    try {
      const sport = getSelectedSport();

      // MLB: Use local data service to avoid CORS issues
      // Note: For MLB, fetchData already fetches and stores stats via MLB API
      // This function only needs to display them, not fetch again
      if (sport === "MLB") {
        const localStats = getMLBPlayerStats(playerId);
        if (localStats?.data?.length > 0) {
          console.log('[MLB] Using local stats data for:', playerId);
          setTableStats(localStats.data);
          setTotalTeamsStats(localStats.total || localStats.data.length);
        } else {
          // No stats available (rookie, minor leaguer, etc.) - that's OK
          console.log('[MLB] No stats available for:', playerId, '(may be rookie or minor leaguer)');
          setTableStats([]);
          setTotalTeamsStats(0);
        }
        setTableLoading(false);
        return;
      }

      // Non-MLB sports: Use API
      const baseUrl = `/${sport}/players/${playerId}/stats`;
      const queryParams = new URLSearchParams({
        page,
        page_size: size,
      });

      // Only append stats_type if sport is CBB
      if (sport === "CBB") {
        queryParams.append("stats_type", stats_type);
      }

      const statsResponse = await apiClient.get(
        `${baseUrl}?${queryParams.toString()}`
      );
      console.log("Stats API response status:", statsResponse?.data?.data);

      // Store raw stats data
      setTableStats(statsResponse?.data?.data);
      setTotalTeamsStats(statsResponse?.data?.total);
    } catch (error) {
      console.error(`Error fetching player details for ${playerId}:`, error);
      setError(`Failed to load player details. ${error.message}`);
    } finally {
      setTableLoading(false);
    }
  };

  const fetchData = async (sport) => {
    setLoading(true);
    setError(null);

    try {
      // MLB: Use local cache + MLB Stats API fallback (no SLAM gateway needed)
      if (sport === "MLB") {
        // Try local cache first (fast path)
        let profile = getMLBPlayerProfile(playerId);
        let stats = getMLBPlayerStats(playerId);

        // If not in local cache, fetch dynamically from MLB Stats API
        if (!profile) {
          console.log('[MLB] No local profile for:', playerId, '- fetching from MLB API');
          profile = await fetchMLBPlayerProfile(playerId);
        } else {
          console.log('[MLB] Using local profile for:', playerId);
        }

        // Fetch stats if profile found and stats not in cache
        if (profile && (!stats?.data || stats.data.length === 0)) {
          console.log('[MLB] Fetching stats from MLB API for:', playerId);
          stats = await fetchMLBPlayerStats(playerId, profile.position);
        }

        if (profile) {
          // Store raw stats data
          setPlayerStats(stats?.data || []);

          // Store season stats
          setSeasonStats(stats?.season_stats || []);

          // Process profile using MLB processor
          const processedPlayer = processMLBProfile(profile, stats?.data);
          setPlayer(processedPlayer);
          setLoading(false);
          return;
        } else {
          console.error('[MLB] Failed to load profile for player:', playerId);
          setError('Player profile not found. The player may not exist in MLB records.');
          setLoading(false);
          return;
        }
      }

      // Non-MLB sports or MLB fallback: Use API
      const [playerResponse, statsResponse] = await Promise.all([
        apiClient.get(`${sport}/players/${playerId}`),
        apiClient.get(`${sport}/players/${playerId}/stats`),
      ]);

      // Store raw stats data
      setPlayerStats(statsResponse?.data?.data);

      // Store season stats from the response
      const seasonStatsData = statsResponse?.data?.season_stats || [];
      setSeasonStats(seasonStatsData);

      // Process player data from the first API response
      let playerData = playerResponse.data;

      // If the response is wrapped in a data or results property
      if (
        !playerData.name &&
        !playerData.first_name &&
        playerResponse.data.data
      ) {
        playerData = playerResponse.data.data;
      } else if (
        !playerData.name &&
        !playerData.first_name &&
        playerResponse.data.results
      ) {
        playerData = playerResponse.data.results;
      }

      // Process based on sport type
      if (sport === "CBB") {
        setPlayer(processBaseballPlayer(playerData, statsResponse?.data?.data));
      } else if (sport === "WBB" || sport === "MBB") {
        setPlayer(
          processBasketballPlayer(playerData, statsResponse?.data?.data)
        );
      } else {
        // Default fallback processing
        setPlayer(processGenericPlayer(playerData, statsResponse?.data?.data));
      }
    } catch (error) {
      console.error(`Error fetching player details for ${playerId}:`, error);
      setError(`Failed to load player details. ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const CareerStatsComponent = ({ seasonStats, sportType, player: playerProp }) => {
    // For MLB/CBB, show baseball stats even without seasonStats
    if (sportType === "MLB" || sportType === "CBB") {
      const isPitcher = ['SP', 'RP', 'P', 'RHP', 'LHP'].includes(playerProp?.position || playerProp?.Position || player?.position || player?.Position);
      const playerData = playerProp || player;

      return (
        <div className="py-4 rounded-lg mb-6">
          <div
            className="bg-[#111111] border-[#1A1A1A] mb-7 shadow-md rounded-[14px] p-6 border transition-colors"
          >
            <h4
              className="text-xl font-bold text-[#F5F5F5] border-[#1A1A1A] mb-4 pb-2 border-b flex items-center gap-2"
            >
              <span>⚾</span> {isPitcher ? 'Pitching Stats' : 'Batting Stats'}
            </h4>

            {/* Primary Stats Grid */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
              {isPitcher ? (
                <>
                  <div className="text-center p-3 rounded-lg" style={{ background: 'rgba(34,197,94,0.1)' }}>
                    <div className="text-2xl font-bold text-green-400">
                      {playerData?.era || playerData?.ERA || 'N/A'}
                    </div>
                    <div className="text-sm text-[#999999]">ERA</div>
                  </div>
                  <div className="text-center p-3 rounded-lg" style={{ background: 'rgba(59,130,246,0.1)' }}>
                    <div className="text-2xl font-bold text-blue-400">
                      {playerData?.whip || playerData?.WHIP || 'N/A'}
                    </div>
                    <div className="text-sm text-[#999999]">WHIP</div>
                  </div>
                  <div className="text-center p-3 rounded-lg" style={{ background: 'rgba(232,184,0,0.1)' }}>
                    <div className="text-2xl font-bold text-[#E8B800]">
                      {playerData?.strikeouts || 'N/A'}
                    </div>
                    <div className="text-sm text-[#999999]">Strikeouts</div>
                  </div>
                  <div className="text-center p-3 rounded-lg" style={{ background: 'rgba(168,85,247,0.1)' }}>
                    <div className="text-2xl font-bold text-purple-400">
                      {playerData?.wins != null ? `${playerData.wins}-${playerData.losses || 0}` : 'N/A'}
                    </div>
                    <div className="text-sm text-[#999999]">W-L</div>
                  </div>
                </>
              ) : (
                <>
                  <div className="text-center p-3 rounded-lg" style={{ background: 'rgba(34,197,94,0.1)' }}>
                    <div className="text-2xl font-bold text-green-400">
                      {playerData?.batting_average || playerData?.AVG || 'N/A'}
                    </div>
                    <div className="text-sm text-[#999999]">AVG</div>
                  </div>
                  <div className="text-center p-3 rounded-lg" style={{ background: 'rgba(59,130,246,0.1)' }}>
                    <div className="text-2xl font-bold text-blue-400">
                      {playerData?.ops || playerData?.OPS || 'N/A'}
                    </div>
                    <div className="text-sm text-[#999999]">OPS</div>
                  </div>
                  <div className="text-center p-3 rounded-lg" style={{ background: 'rgba(232,184,0,0.1)' }}>
                    <div className="text-2xl font-bold text-[#E8B800]">
                      {playerData?.home_runs || 'N/A'}
                    </div>
                    <div className="text-sm text-[#999999]">Home Runs</div>
                  </div>
                  <div className="text-center p-3 rounded-lg" style={{ background: 'rgba(168,85,247,0.1)' }}>
                    <div className="text-2xl font-bold text-purple-400">
                      {playerData?.rbi || 'N/A'}
                    </div>
                    <div className="text-sm text-[#999999]">RBI</div>
                  </div>
                </>
              )}
            </div>

            {/* Statcast Metrics Section */}
            <div
              className="p-4 rounded-lg border"
              style={{
                background: '#111111',
                borderColor: 'rgba(59,130,246,0.2)',
              }}
            >
              <h5 className="text-sm font-semibold text-blue-400 mb-3 flex items-center gap-2">
                <span>📊</span> Statcast Metrics
              </h5>
              <div className="grid grid-cols-3 gap-4">
                {isPitcher ? (
                  <>
                    <div className="text-center">
                      <div className="text-lg font-bold text-blue-300">
                        {playerData?.avgFastball ? `${playerData.avgFastball} mph` : 'N/A'}
                      </div>
                      <div className="text-xs text-[#555555]">Avg Fastball</div>
                    </div>
                    <div className="text-center">
                      <div className="text-lg font-bold text-blue-300">
                        {playerData?.spinRate || 'N/A'}
                      </div>
                      <div className="text-xs text-[#555555]">Spin Rate</div>
                    </div>
                    <div className="text-center">
                      <div className="text-lg font-bold text-blue-300">
                        {playerData?.whiffRate ? `${playerData.whiffRate}%` : 'N/A'}
                      </div>
                      <div className="text-xs text-[#555555]">Whiff Rate</div>
                    </div>
                  </>
                ) : (
                  <>
                    <div className="text-center">
                      <div className="text-lg font-bold text-blue-300">
                        {playerData?.exitVelocity ? `${playerData.exitVelocity} mph` : 'N/A'}
                      </div>
                      <div className="text-xs text-[#555555]">Exit Velocity</div>
                    </div>
                    <div className="text-center">
                      <div className="text-lg font-bold text-blue-300">
                        {playerData?.launchAngle ? `${playerData.launchAngle}°` : 'N/A'}
                      </div>
                      <div className="text-xs text-[#555555]">Launch Angle</div>
                    </div>
                    <div className="text-center">
                      <div className="text-lg font-bold text-blue-300">
                        {playerData?.barrelRate ? `${playerData.barrelRate}%` : 'N/A'}
                      </div>
                      <div className="text-xs text-[#555555]">Barrel Rate</div>
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      );
    }

    if (!seasonStats || seasonStats.length === 0) {
      return (
        <div
          className="mb-4 bg-[#111111] border border-[#1A1A1A] p-4 rounded-[14px] transition-colors"
        >
          <h3
            className="font-bold text-[#F5F5F5] mb-3"
          >
            Career Stats
          </h3>
          <p
            className="text-[#999999]"
          >
            No career statistics available
          </p>
        </div>
      );
    }

    // Aggregate career stats across all seasons
    const careerStats = seasonStats.reduce((acc, season) => {
      return {
        gamesPlayed: (acc.gamesPlayed || 0) + (season.GamesPlayed || 0),
        minutesTotal: (acc.minutesTotal || 0) + (season.MinutesTotal || 0),
        fieldGoalsMade:
          (acc.fieldGoalsMade || 0) + (season.FieldGoalsMade || 0),
        fieldGoalsAttempted:
          (acc.fieldGoalsAttempted || 0) + (season.FieldGoalsAttempted || 0),
        threePointMade:
          (acc.threePointMade || 0) + (season.ThreePointMade || 0),
        threePointAttempted:
          (acc.threePointAttempted || 0) + (season.ThreePointAttempted || 0),
        freeThrowsMade:
          (acc.freeThrowsMade || 0) + (season.FreeThrowsMade || 0),
        freeThrowsAttempted:
          (acc.freeThrowsAttempted || 0) + (season.FreeThrowsAttempted || 0),
        totalRebounds: (acc.totalRebounds || 0) + (season.TotalRebounds || 0),
        assists: (acc.assists || 0) + (season.Assists || 0),
        blocks: (acc.blocks || 0) + (season.Blocks || 0),
        steals: (acc.steals || 0) + (season.Steals || 0),
        personalFouls: (acc.personalFouls || 0) + (season.PersonalFouls || 0),
        turnovers: (acc.turnovers || 0) + (season.Turnovers || 0),
        totalPoints: (acc.totalPoints || 0) + (season.TotalPoints || 0),
      };
    }, {});

    // Calculate percentages
    const fgPercentage =
      careerStats.fieldGoalsAttempted > 0
        ? (
          (careerStats.fieldGoalsMade / careerStats.fieldGoalsAttempted) *
          100
        ).toFixed(1)
        : "0.0";

    const threePtPercentage =
      careerStats.threePointAttempted > 0
        ? (
          (careerStats.threePointMade / careerStats.threePointAttempted) *
          100
        ).toFixed(1)
        : "0.0";

    const ftPercentage =
      careerStats.freeThrowsAttempted > 0
        ? (
          (careerStats.freeThrowsMade / careerStats.freeThrowsAttempted) *
          100
        ).toFixed(1)
        : "0.0";

    // Calculate per-game averages
    const minutesPerGame =
      careerStats.gamesPlayed > 0
        ? (careerStats.minutesTotal / careerStats.gamesPlayed).toFixed(1)
        : "0.0";

    const pointsPerGame =
      careerStats.gamesPlayed > 0
        ? (careerStats.totalPoints / careerStats.gamesPlayed).toFixed(1)
        : "0.0";

    const reboundsPerGame =
      careerStats.gamesPlayed > 0
        ? (careerStats.totalRebounds / careerStats.gamesPlayed).toFixed(1)
        : "0.0";

    const assistsPerGame =
      careerStats.gamesPlayed > 0
        ? (careerStats.assists / careerStats.gamesPlayed).toFixed(1)
        : "0.0";

    const blocksPerGame =
      careerStats.gamesPlayed > 0
        ? (careerStats.blocks / careerStats.gamesPlayed).toFixed(1)
        : "0.0";

    const stealsPerGame =
      careerStats.gamesPlayed > 0
        ? (careerStats.steals / careerStats.gamesPlayed).toFixed(1)
        : "0.0";

    const foulsPerGame =
      careerStats.gamesPlayed > 0
        ? (careerStats.personalFouls / careerStats.gamesPlayed).toFixed(1)
        : "0.0";

    const turnoversPerGame =
      careerStats.gamesPlayed > 0
        ? (careerStats.turnovers / careerStats.gamesPlayed).toFixed(1)
        : "0.0";

    // Get unique seasons for display
    const seasons = seasonStats.map((s) => s.Season).sort((a, b) => b - a);
    const seasonsText =
      seasons.length > 1
        ? `${Math.min(...seasons)}-${Math.max(...seasons)}`
        : seasons[0] || "Career";

    if (sportType === "MBB" || sportType === "WBB") {
      return (
        <div className="py-4 rounded-lg mb-6">
          {seasonStats && seasonStats.length > 0 && (
            <div>
              {/* <PlayerPerformanceRadarChart  seasonStats={seasonStats} /> */}
              <h4
                className="text-xl font-bold text-[#F5F5F5] mb-4 pb-2"
                style={{ textAlign: "center" }}
              >
                Performance Radar Chart
              </h4>
              <BasketballRadarChart seasonStats={seasonStats} />
            </div>
          )}
          <div
            className="bg-[#111111] border-[#1A1A1A] mb-7 shadow-md rounded-[14px] p-6 border transition-colors"
          >
            <h4
              className="text-xl font-bold text-[#F5F5F5] border-[#1A1A1A] mb-4 pb-2 border-b"
            >
              Career Totals
            </h4>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div
                className="bg-[#111111] border border-[#1A1A1A] rounded-md p-4 text-center hover:shadow-md transition-all duration-300 ease-in-out transform hover:-translate-y-1"
              >
                <div className="text-2xl font-extrabold text-[#E8B800] mb-1">
                  {(careerStats.totalPoints ?? 0).toLocaleString()}
                </div>
                <div
                  className="text-xs uppercase tracking-wider text-[#999999]"
                >
                  Total Points
                </div>
              </div>
              <div
                className="bg-[#111111] border border-[#1A1A1A] rounded-md p-4 text-center hover:shadow-md transition-all duration-300 ease-in-out transform hover:-translate-y-1"
              >
                <div className="text-2xl font-extrabold text-green-400 mb-1">
                  {(careerStats.totalRebounds ?? 0).toLocaleString()}
                </div>
                <div
                  className="text-xs uppercase tracking-wider text-[#999999]"
                >
                  Total Rebounds
                </div>
              </div>
              <div
                className="bg-[#111111] border border-[#1A1A1A] rounded-md p-4 text-center hover:shadow-md transition-all duration-300 ease-in-out transform hover:-translate-y-1"
              >
                <div className="text-2xl font-extrabold text-purple-400 mb-1">
                  {(careerStats.assists ?? 0).toLocaleString()}
                </div>
                <div
                  className="text-xs uppercase tracking-wider text-[#999999]"
                >
                  Total Assists
                </div>
              </div>
              <div
                className="bg-[#111111] border border-[#1A1A1A] rounded-md p-4 text-center hover:shadow-md transition-all duration-300 ease-in-out transform hover:-translate-y-1"
              >
                <div className="text-2xl font-extrabold text-cyan-400 mb-1">
                  {(careerStats.gamesPlayed ?? 0).toLocaleString()}
                </div>
                <div
                  className="text-xs uppercase tracking-wider text-[#999999]"
                >
                  Games Played
                </div>
              </div>
            </div>
          </div>

          <h3
            className="font-bold text-[#F5F5F5] mb-4"
          >
            Career Stats {/* ({seasonsText}) */}
          </h3>

          {/* Career Stats Table */}
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead
                className="bg-[#111111] text-[#F5F5F5] text-xs uppercase transition-colors"
              >
                <tr className="">
                  <th
                    className={`py-3 px-4 text-left border-b border-[#1A1A1A]`}
                  >
                    STATS
                  </th>
                  <th
                    className={`py-3 px-4 text-left border-b border-[#1A1A1A]`}
                  >
                    GP
                  </th>
                  <th
                    className={`py-3 px-4 text-left border-b border-[#1A1A1A]`}
                  >
                    MIN
                  </th>
                  <th
                    className={`py-3 px-4 text-left border-b border-[#1A1A1A]`}
                  >
                    FG%
                  </th>
                  <th
                    className={`py-3 px-4 text-left border-b border-[#1A1A1A]`}
                  >
                    3P%
                  </th>
                  <th
                    className={`py-3 px-4 text-left border-b border-[#1A1A1A]`}
                  >
                    FT%
                  </th>
                  <th
                    className={`py-3 px-4 text-left border-b border-[#1A1A1A]`}
                  >
                    REB
                  </th>
                  <th
                    className={`py-3 px-4 text-left border-b border-[#1A1A1A]`}
                  >
                    AST
                  </th>
                  <th
                    className={`py-3 px-4 text-left border-b border-[#1A1A1A]`}
                  >
                    BLK
                  </th>
                  <th
                    className={`py-3 px-4 text-left border-b border-[#1A1A1A]`}
                  >
                    STL
                  </th>
                  <th
                    className={`py-3 px-4 text-left border-b border-[#1A1A1A]`}
                  >
                    PF
                  </th>
                  <th
                    className={`py-3 px-4 text-left border-b border-[#1A1A1A]`}
                  >
                    TO
                  </th>
                  <th
                    className={`py-3 px-4 text-left border-b border-[#1A1A1A]`}
                  >
                    PTS
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr
                  className={`hover:bg-[rgba(255,255,255,0.04)] transition-colors`}
                >
                  <td
                    className={`py-3 px-4 border-b border-[#1A1A1A]`}
                  >
                    Career
                  </td>
                  <td
                    className={`py-3 px-4 border-b border-[#1A1A1A]`}
                  >
                    {careerStats.gamesPlayed || 0}
                  </td>
                  <td
                    className={`py-3 px-4 border-b border-[#1A1A1A]`}
                  >
                    {minutesPerGame}
                  </td>
                  <td
                    className={`py-3 px-4 border-b border-[#1A1A1A]`}
                  >
                    {fgPercentage}
                  </td>
                  <td
                    className={`py-3 px-4 border-b border-[#1A1A1A]`}
                  >
                    {threePtPercentage}
                  </td>
                  <td
                    className={`py-3 px-4 border-b border-[#1A1A1A]`}
                  >
                    {ftPercentage}
                  </td>
                  <td
                    className={`py-3 px-4 border-b border-[#1A1A1A]`}
                  >
                    {reboundsPerGame}
                  </td>
                  <td
                    className={`py-3 px-4 border-b border-[#1A1A1A]`}
                  >
                    {assistsPerGame}
                  </td>
                  <td
                    className={`py-3 px-4 border-b border-[#1A1A1A]`}
                  >
                    {blocksPerGame}
                  </td>
                  <td
                    className={`py-3 px-4 border-b border-[#1A1A1A]`}
                  >
                    {stealsPerGame}
                  </td>
                  <td
                    className={`py-3 px-4 border-b border-[#1A1A1A]`}
                  >
                    {foulsPerGame}
                  </td>
                  <td
                    className={`py-3 px-4 border-b border-[#1A1A1A]`}
                  >
                    {turnoversPerGame}
                  </td>
                  <td
                    className={`py-3 px-4 border-b border-[#1A1A1A]`}
                  >
                    {pointsPerGame}
                  </td>
                </tr>

                {/* Individual season rows */}
                {seasonStats
                  .sort((a, b) => b.Season - a.Season)
                  .map((season, index) => {
                    const seasonFgPct =
                      season.FieldGoalsAttempted > 0
                        ? (
                          (season.FieldGoalsMade /
                            season.FieldGoalsAttempted) *
                          100
                        ).toFixed(1)
                        : "0.0";

                    const season3PtPct =
                      season.ThreePointAttempted > 0
                        ? (
                          (season.ThreePointMade /
                            season.ThreePointAttempted) *
                          100
                        ).toFixed(1)
                        : "0.0";

                    const seasonFtPct =
                      season.FreeThrowsAttempted > 0
                        ? (
                          (season.FreeThrowsMade /
                            season.FreeThrowsAttempted) *
                          100
                        ).toFixed(1)
                        : "0.0";

                    return (
                      <tr
                        key={index}
                        className={`hover:bg-[rgba(255,255,255,0.04)] transition-colors`}
                      >
                        <td
                          className={`py-3 px-4 border-b border-[#1A1A1A]`}
                        >
                          {season.Season || "N/A"}
                        </td>
                        <td
                          className={`py-3 px-4 border-b border-[#1A1A1A]`}
                        >
                          {season.GamesPlayed || 0}
                        </td>
                        <td
                          className={`py-3 px-4 border-b border-[#1A1A1A]`}
                        >
                          {season.MinutesPerGame?.toFixed(1) || "0.0"}
                        </td>
                        <td
                          className={`py-3 px-4 border-b border-[#1A1A1A]`}
                        >
                          {seasonFgPct}
                        </td>
                        <td
                          className={`py-3 px-4 border-b border-[#1A1A1A]`}
                        >
                          {season3PtPct}
                        </td>
                        <td
                          className={`py-3 px-4 border-b border-[#1A1A1A]`}
                        >
                          {seasonFtPct}
                        </td>
                        <td
                          className={`py-3 px-4 border-b border-[#1A1A1A]`}
                        >
                          {season.ReboundsPerGame?.toFixed(1) || "0.0"}
                        </td>
                        <td
                          className={`py-3 px-4 border-b border-[#1A1A1A]`}
                        >
                          {season.AssistsPerGame?.toFixed(1) || "0.0"}
                        </td>
                        <td
                          className={`py-3 px-4 border-b border-[#1A1A1A]`}
                        >
                          {season.BlocksPerGame?.toFixed(1) || "0.0"}
                        </td>
                        <td
                          className={`py-3 px-4 border-b border-[#1A1A1A]`}
                        >
                          {season.StealsPerGame?.toFixed(1) || "0.0"}
                        </td>
                        <td
                          className={`py-3 px-4 border-b border-[#1A1A1A]`}
                        >
                          {season.PersonalFoulsPerGame?.toFixed(1) || "0.0"}
                        </td>
                        <td
                          className={`py-3 px-4 border-b border-[#1A1A1A]`}
                        >
                          {season.TurnoversPerGame?.toFixed(1) || "0.0"}
                        </td>
                        <td
                          className={`py-3 px-4 border-b border-[#1A1A1A]`}
                        >
                          {season.PointsPerGame?.toFixed(1) || "0.0"}
                        </td>
                      </tr>
                    );
                  })}
              </tbody>
            </table>
          </div>

          {/* Career Totals Summary */}
        </div>
      );
    }

    // For other sports, return a generic career stats display
    return (
      <div
        className="bg-[#111111] border border-[#1A1A1A] p-4 rounded-[14px] mb-6 transition-colors"
      >
        <h3
          className="font-bold text-[#F5F5F5] mb-3"
        >
          Career Stats ({seasonsText})
        </h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="text-center">
            <div className="text-2xl font-bold text-[#E8B800]">
              {careerStats.gamesPlayed || 0}
            </div>
            <div className="text-sm text-[#999999]">
              Games
            </div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold text-cyan-400">
              {careerStats.totalPoints || 0}
            </div>
            <div className="text-sm text-[#999999]">
              Points
            </div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold text-green-400">
              {pointsPerGame}
            </div>
            <div className="text-sm text-[#999999]">
              PPG
            </div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold text-purple-400">
              {careerStats.totalRebounds || 0}
            </div>
            <div className="text-sm text-[#999999]">
              Rebounds
            </div>
          </div>
        </div>
      </div>
    );
  };
  // Process baseball player data
  const processBaseballPlayer = (playerData, statsData) => {
    // Aggregate baseball stats across all games
    const aggregatedStats = statsData.reduce(
      (acc, game) => {
        return {
          totalGames: acc.totalGames + 1,
          totalAB: acc.totalAB + (game.AB || 0),
          totalR: acc.totalR + (game.R || 0),
          totalH: acc.totalH + (game.H || 0),
          totalRBI: acc.totalRBI + (game.RBI || 0),
          totalHR: acc.totalHR + (game.HR || 0),
          totalBB: acc.totalBB + (game.BB || 0),
          totalSO: acc.totalSO + (game.SO || 0),
          // Pitcher stats
          totalIP: acc.totalIP + (game.IP || 0),
          totalBF: acc.totalBF + (game.BF || 0),
          totalER: acc.totalER + (game.ER || 0),
          totalPIT: acc.totalPIT + (game.PIT || 0),
          totalW: acc.totalW + (game.W || 0),
          totalL: acc.totalL + (game.L || 0),
          totalSV: acc.totalSV + (game.SV || 0),
        };
      },
      {
        totalGames: 0,
        totalAB: 0,
        totalR: 0,
        totalH: 0,
        totalRBI: 0,
        totalHR: 0,
        totalBB: 0,
        totalSO: 0,
        totalIP: 0,
        totalBF: 0,
        totalER: 0,
        totalPIT: 0,
        totalW: 0,
        totalL: 0,
        totalSV: 0,
      }
    );

    // Calculate derived statistics
    const battingAverage =
      aggregatedStats.totalAB > 0
        ? (aggregatedStats.totalH / aggregatedStats.totalAB).toFixed(3)
        : ".000";

    const onBasePercentage =
      aggregatedStats.totalAB + aggregatedStats.totalBB > 0
        ? (
          (aggregatedStats.totalH + aggregatedStats.totalBB) /
          (aggregatedStats.totalAB + aggregatedStats.totalBB)
        ).toFixed(3)
        : ".000";

    const sluggingPercentage =
      aggregatedStats.totalAB > 0
        ? (
          (aggregatedStats.totalH + aggregatedStats.totalHR) /
          aggregatedStats.totalAB
        ).toFixed(3)
        : ".000";

    const ops = (
      parseFloat(onBasePercentage) + parseFloat(sluggingPercentage)
    ).toFixed(3);

    const era =
      aggregatedStats.totalIP > 0
        ? ((aggregatedStats.totalER * 9) / aggregatedStats.totalIP).toFixed(2)
        : "0.00";

    // Determine player type (pitcher or position player)
    const isPitcher =
      statsData.some((game) => game.IP > 0) ||
      (playerData.position && playerData.position.toLowerCase().includes("p"));

    // Determine special attributes
    const specialAttrs = [];
    if (isPitcher) {
      if (parseFloat(era) < 3.0) {
        specialAttrs.push("TOP PITCHER");
      } else if (aggregatedStats.totalSO > 15) {
        specialAttrs.push("STRIKEOUT ARTIST");
      } else {
        specialAttrs.push("PITCHER");
      }
    } else {
      if (parseFloat(battingAverage) > 0.3) {
        specialAttrs.push("CONTACT HITTER");
      } else if (aggregatedStats.totalHR > 5) {
        specialAttrs.push("POWER HITTER");
      } else if (aggregatedStats.totalRBI > 20) {
        specialAttrs.push("RUN PRODUCER");
      } else {
        specialAttrs.push("VERSATILE");
      }
    }

    // Get team info from stats if available
    let team = playerData.team || playerData.team_name || "Unknown Team";
    let teamAbbrev = "";

    if (statsData.length > 0 && statsData[0].Team) {
      team = statsData[0].Team;
      teamAbbrev = statsData[0].TeamAbbrev || "";
    }

    return {
      id: playerData.id || playerData.player_id || playerId,
      position: playerData.position || playerData.Position || "Unknown",
      fullName:
        playerData.name ||
        `${playerData.first_name || ""} ${playerData.last_name || ""}`.trim(),
      firstName: playerData.first_name || playerData.name?.split(" ")[0] || "",
      lastName:
        playerData.last_name ||
        playerData.name?.split(" ").slice(1).join(" ") ||
        "",
      year: playerData.year || playerData.class_year || "Unknown",
      height: playerData.height || playerData?.Height || "",
      Player: playerData?.Player || playerData?.name,
      weight: playerData.weight || playerData?.Weight || "",
      team: team,
      teamAbbrev: teamAbbrev,
      // Baseball specific stats
      battingAverage: battingAverage,
      homeRuns: aggregatedStats.totalHR,
      ops: ops,
      exitVelo: playerData.exit_velocity || Math.floor(Math.random() * 15 + 85),
      swingMiss: playerData.swing_miss_rate || Math.floor(Math.random() * 30),
      speed: playerData.speed || (Math.random() * 5 + 25).toFixed(1),
      imageUrl: playerData.image_url || playerData.photo || "/SLAM_logo.png",
      specialAttrs: specialAttrs,
      obp: onBasePercentage,
      slg: sluggingPercentage,
      games: aggregatedStats.totalGames,
      atBats: aggregatedStats.totalAB,
      runs: aggregatedStats.totalR,
      hits: aggregatedStats.totalH,
      rbi: aggregatedStats.totalRBI,
      // Pitcher stats
      era: era,
      ip: aggregatedStats.totalIP,
      bf: aggregatedStats.totalBF,
      er: aggregatedStats.totalER,
      bb: aggregatedStats.totalBB,
      so: aggregatedStats.totalSO,
      pit: aggregatedStats.totalPIT,
      wins: aggregatedStats.totalW,
      losses: aggregatedStats.totalL,
      saves: aggregatedStats.totalSV,
      bio: playerData.bio || playerData.biography || "No biography available.",
      highlights: playerData.highlights || [],
      isPitcher: isPitcher,
    };
  };

  // Process basketball player data
  const processBasketballPlayer = (playerData, statsData) => {
    // Aggregate basketball stats across all games
    const aggregatedStats = statsData.reduce(
      (acc, game) => {
        return {
          totalGames: acc.totalGames + 1,
          totalMIN: acc.totalMIN + (game.MIN || 0),
          totalPTS: acc.totalPTS + (game.PTS || 0),
          totalFGM: acc.totalFGM + (game.FGM || 0),
          totalFGA: acc.totalFGA + (game.FGA || 0),
          total3PM: acc.total3PM + (game.ThreePM || 0),
          total3PA: acc.total3PA + (game.ThreePA || 0),
          totalFTM: acc.totalFTM + (game.FTM || 0),
          totalFTA: acc.totalFTA + (game.FTA || 0),
          totalREB: acc.totalREB + (game.REB || 0),
          totalOREG: acc.totalOREG + (game.OREB || 0),
          totalAST: acc.totalAST + (game.AST || 0),
          totalSTL: acc.totalSTL + (game.STL || 0),
          totalBLK: acc.totalBLK + (game.BLK || 0),
          totalTO: acc.totalTO + (game.TO || 0),
          totalPF: acc.totalPF + (game.PF || 0),
        };
      },
      {
        totalGames: 0,
        totalMIN: 0,
        totalPTS: 0,
        totalFGM: 0,
        totalFGA: 0,
        total3PM: 0,
        total3PA: 0,
        totalFTM: 0,
        totalFTA: 0,
        totalREB: 0,
        totalOREG: 0,
        totalAST: 0,
        totalSTL: 0,
        totalBLK: 0,
        totalTO: 0,
        totalPF: 0,
      }
    );

    // Calculate derived statistics
    const fgPercentage =
      aggregatedStats.totalFGA > 0
        ? (aggregatedStats.totalFGM / aggregatedStats.totalFGA).toFixed(3)
        : ".000";

    const ftPercentage =
      aggregatedStats.totalFTA > 0
        ? (aggregatedStats.totalFTM / aggregatedStats.totalFTA).toFixed(3)
        : ".000";

    const threePtPercentage =
      aggregatedStats.total3PA > 0
        ? (aggregatedStats.total3PM / aggregatedStats.total3PA).toFixed(3)
        : ".000";

    const ppg =
      aggregatedStats.totalGames > 0
        ? (aggregatedStats.totalPTS / aggregatedStats.totalGames).toFixed(1)
        : "0.0";

    const rpg =
      aggregatedStats.totalGames > 0
        ? (aggregatedStats.totalREB / aggregatedStats.totalGames).toFixed(1)
        : "0.0";

    const apg =
      aggregatedStats.totalGames > 0
        ? (aggregatedStats.totalAST / aggregatedStats.totalGames).toFixed(1)
        : "0.0";

    // Determine special attributes
    const specialAttrs = [];
    if (parseFloat(ppg) > 15) {
      specialAttrs.push("SCORER");
    } else if (parseFloat(rpg) > 8) {
      specialAttrs.push("REBOUNDER");
    } else if (parseFloat(apg) > 5) {
      specialAttrs.push("PLAYMAKER");
    } else if (aggregatedStats.totalSTL > 10) {
      specialAttrs.push("DEFENDER");
    } else if (aggregatedStats.totalBLK > 10) {
      specialAttrs.push("SHOT BLOCKER");
    } else {
      specialAttrs.push("TEAM PLAYER");
    }

    // Get team info from stats
    let team = playerData.team || playerData.team_name || "Unknown Team";
    let teamAbbrev = "";

    if (statsData.length > 0 && statsData[0].Team) {
      team = statsData[0].Team;
    }

    // Get player position (if available)
    let position = playerData.Position || "Unknown";

    // For basketball positions, they might be labeled differently
    // Common basketball positions: PG, SG, SF, PF, C
    if (!position || position === "Unknown") {
      // Try to infer position from stats
      if (parseFloat(apg) > 5) {
        position = "PG"; // Point Guard
      } else if (parseFloat(ppg) > 15 && parseFloat(rpg) < 6) {
        position = "SG"; // Shooting Guard
      } else if (parseFloat(rpg) > 8 && aggregatedStats.totalBLK > 10) {
        position = "C"; // Center
      } else if (parseFloat(rpg) > 6) {
        position = "PF"; // Power Forward
      } else {
        position = "SF"; // Small Forward
      }
    }

    return {
      id: playerData.id || playerData.player_id || playerId,
      position: playerData.Position || "Unknown",
      fullName:
        playerData.name ||
        statsData[0]?.Player ||
        `${playerData.first_name || ""} ${playerData.last_name || ""}`.trim(),
      firstName: playerData.first_name || playerData.name?.split(" ")[0] || "",
      lastName:
        playerData.last_name ||
        playerData.name?.split(" ").slice(1).join(" ") ||
        "",
      year: playerData.year || playerData.class_year || "Unknown",
      height: playerData.height || playerData.Height || "",
      weight: playerData.weight || playerData.Weight || "",
      team: team,
      teamAbbrev: teamAbbrev,
      Player: playerData?.Player || playerData?.name,
      // Basketball specific stats
      ppg: ppg,
      rpg: rpg,
      apg: apg,
      fgPercentage: fgPercentage,
      ftPercentage: ftPercentage,
      threePtPercentage: threePtPercentage,
      imageUrl: playerData.image_url || playerData.photo || "/SLAM_logo.png",
      specialAttrs: specialAttrs,
      games: aggregatedStats.totalGames,
      minutes: aggregatedStats.totalMIN,
      points: aggregatedStats.totalPTS,
      fgm: aggregatedStats.totalFGM,
      fga: aggregatedStats.totalFGA,
      threePm: aggregatedStats.total3PM,
      threePa: aggregatedStats.total3PA,
      ftm: aggregatedStats.totalFTM,
      fta: aggregatedStats.totalFTA,
      rebounds: aggregatedStats.totalREB,
      offRebounds: aggregatedStats.totalOREG,
      assists: aggregatedStats.totalAST,
      steals: aggregatedStats.totalSTL,
      blocks: aggregatedStats.totalBLK,
      turnovers: aggregatedStats.totalTO,
      fouls: aggregatedStats.totalPF,
      bio: playerData.bio || playerData.biography || "No biography available.",
      highlights: playerData.highlights || [],
    };
  };

  // Fallback processor for other sports or generic player profiles
  const processGenericPlayer = (playerData, statsData) => {
    return {
      id: playerData.id || playerData.player_id || playerId,
      position: playerData.Position || "Unknown",
      fullName:
        playerData.name ||
        `${playerData.first_name || ""} ${playerData.last_name || ""}`.trim(),
      firstName: playerData.first_name || playerData.name?.split(" ")[0] || "",
      lastName:
        playerData.last_name ||
        playerData.name?.split(" ").slice(1).join(" ") ||
        "",
      year: playerData.year || playerData.class_year || "Unknown",
      height: playerData.Height || "",
      weight: playerData.Weight || "",
      team: playerData.team || playerData.team_name || "Unknown Team",
      teamAbbrev: "",
      imageUrl: playerData.image_url || playerData.photo || "/SLAM_logo.png",
      specialAttrs: ["ATHLETE"],
      bio: playerData.bio || playerData.biography || "No biography available.",
      highlights: playerData.highlights || [],
    };
  };

  // Helper function to determine gradient colors based on position and sport
  const getPositionColors = (position, sport) => {
    if (!position) return "from-blue-600 to-indigo-700";

    position = position.toLowerCase();

    if (sport === "CBB") {
      // Baseball position colors
      if (
        position.includes("p") ||
        position.includes("rh") ||
        position.includes("lh")
      ) {
        return "from-blue-600 to-indigo-700";
      } else if (position.includes("c")) {
        return "from-amber-500 to-orange-600";
      } else if (
        position.includes("ss") ||
        position.includes("2b") ||
        position.includes("3b") ||
        position.includes("1b")
      ) {
        return "from-red-500 to-pink-600";
      } else if (
        position.includes("of") ||
        position.includes("lf") ||
        position.includes("cf") ||
        position.includes("rf")
      ) {
        return "from-green-600 to-teal-500";
      } else {
        return "from-purple-600 to-indigo-600";
      }
    } else if (sport === "WBB" || sport === "MBB") {
      // Basketball position colors
      if (position.includes("pg")) {
        return "from-yellow-500 to-amber-600"; // Point Guard
      } else if (position.includes("sg")) {
        return "from-orange-500 to-red-600"; // Shooting Guard
      } else if (position.includes("sf")) {
        return "from-green-500 to-emerald-600"; // Small Forward
      } else if (position.includes("pf")) {
        return "from-blue-500 to-indigo-600"; // Power Forward
      } else if (position.includes("c")) {
        return "from-purple-600 to-violet-700"; // Center
      } else {
        return "from-gray-600 to-gray-800";
      }
    } else {
      return "from-blue-600 to-indigo-700";
    }
  };

  // Helper function to get position badge color
  const getPositionBadgeColor = (position, sport) => {
    if (!position) return "bg-indigo-600";

    position = position.toLowerCase();

    if (sport === "CBB") {
      // Baseball position badge colors
      if (
        position.includes("p") ||
        position.includes("rh") ||
        position.includes("lh")
      ) {
        return "bg-indigo-600";
      } else if (position.includes("c")) {
        return "bg-amber-600";
      } else if (
        position.includes("ss") ||
        position.includes("2b") ||
        position.includes("3b") ||
        position.includes("1b")
      ) {
        return "bg-red-600";
      } else if (
        position.includes("of") ||
        position.includes("lf") ||
        position.includes("cf") ||
        position.includes("rf")
      ) {
        return "themePrimary";
      } else {
        return "bg-purple-600";
      }
    } else if (sport === "WBB" || sport === "MBB") {
      // Basketball position badge colors
      if (position.includes("pg")) {
        return "bg-amber-600"; // Point Guard
      } else if (position.includes("sg")) {
        return "bg-red-600"; // Shooting Guard
      } else if (position.includes("sf")) {
        return "bg-emerald-600"; // Small Forward
      } else if (position.includes("pf")) {
        return "bg-indigo-600"; // Power Forward
      } else if (position.includes("c")) {
        return "bg-violet-700"; // Center
      } else {
        return "bg-zinc-700";
      }
    } else {
      return "bg-indigo-600";
    }
  };

  // Helper function to get sport badge color and label
  const getSportBadge = (sport) => {
    if (sport === "MLB") {
      return { color: "bg-blue-600", label: "MLB" };
    } else if (sport === "CBB") {
      return { color: "themeSecondary", label: "BASEBALL" };
    } else if (sport === "WBB" || sport === "MBB") {
      return { color: "bg-orange-600", label: "BASKETBALL" };
    } else {
      return { color: "bg-purple-600", label: "UNKNOWN" };
    }
  };

  if (loading) {
    return (
      <div
        className="flex justify-center items-center p-8 bg-[#0A0A0A] transition-colors"
      >
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[#E8B800]"></div>
        <p className="ml-3 text-[#999999]">
          Loading player details...
        </p>
      </div>
    );
  }

  if (error) {
    return (
      <div
        className="bg-red-900 border border-red-700 text-red-300 px-4 py-3 rounded relative mb-6"
      >
        <strong className="font-bold">Error: </strong>
        <span className="block sm:inline">{error}</span>
        <button
          onClick={() => navigate(-1)}
          className="mt-3 bg-[#111111] text-red-400 hover:bg-red-900 border border-[#1A1A1A] px-4 py-2 rounded shadow transition-colors"
        >
          Go Back
        </button>
      </div>
    );
  }

  // if (!player) {
  //   return (
  //     <div
  //       className="text-center py-8 bg-[#0A0A0A] transition-colors"
  //     >
  //       <p
  //         className="text-[#999999]"
  //       >
  //         Player not found
  //       </p>
  //       <button
  //         onClick={() => navigate(-1)}
  //         className="mt-3 bg-[#111111] text-red-400 px-4 py-2 rounded shadow hover:bg-[rgba(255,255,255,0.04)] transition-colors"
  //       >
  //         Go Back
  //       </button>
  //     </div>
  //   );
  // }

  const sportBadge = getSportBadge(sportType);

  return (
    <div
      className="container mx-auto px-4 py-8 bg-[#0A0A0A] text-white transition-colors"
    >
      <div className="flex flex-row justify-between items-center mb-3">
        <div>
          <button
            onClick={() => navigate(-1)}
            className="mb-0 flex items-center text-blue-600 hover:text-blue-800"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-5 w-5 mr-1"
              viewBox="0 0 20 20"
              fill="currentColor"
            >
              <path
                fillRule="evenodd"
                d="M9.707 16.707a1 1 0 01-1.414 0l-6-6a1 1 0 010-1.414l6-6a1 1 0 011.414 1.414L5.414 9H17a1 1 0 110 2H5.414l4.293 4.293a1 1 0 010 1.414z"
                clipRule="evenodd"
              />
            </svg>
            Back to Roster
          </button>
        </div>

        <div className="flex">
          <button
            onClick={(e) => {
              e.stopPropagation();
              // navigate(`/video-analytics?player_id=${playerId}`);
              playerId ? navigate(`/integrations?player_id=${playerId}&is_video_analytics=${true}`) : navigate(`/integrations?is_video_analytics=${true}`);
            }}
            className="ml-5 px-4 py-2 rounded-md flex items-center themeSecondary text-white hoverintoGray transition-colors"
          >
            <Video size={20} />
            <span className="pl-2 font-medium">Video Analytics</span>
          </button>
        </div>
      </div>

      <div
        className="bg-[#0A0A0A] border border-[#1A1A1A] rounded-[14px] shadow-lg overflow-hidden mb-8 transition-colors"
      >
        <div className="h-40 bg-black relative">
          <img
            src={player.imageUrl || "/SLAM_logo.png"}
            alt={player.fullName}
            className="w-full h-full object-cover opacity-85"
            onError={(e) => {
              e.target.onerror = null;
              e.target.src = "/SLAM_logo.png";
              e.target.classList.add("p-12");
              e.target.classList.remove("opacity-85");
              e.target.classList.add("opacity-100");
              e.target.style.objectFit = "contain";
            }}
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent"></div>
          <div className="absolute top-0 left-0 p-4 z-10 flex items-center gap-2">
            <span
              className={`${sportBadge.color} text-white text-sm font-bold px-3 py-1 rounded-md uppercase`}
            >
              {sportBadge.label}
            </span>
            <span
              className={`${getPositionBadgeColor(
                player.position,
                sportType
              )} text-white text-sm font-bold px-3 py-1 rounded-md uppercase`}
            >
              {player.position}
            </span>
          </div>
        </div>

        <div
          className="bg-[#0A0A0A] rounded-[14px] mx-auto relative transition-colors"
          style={{ width: "95%", marginTop: "-90px", zIndex: 1 }}
        >
          {/* Header Image */}
          <div className="border rounded-md border-[#1A1A1A]">
            <div
              className="flex items-center space-x-4 rounded-md bg-[#0A0A0A] transition-colors"
            >
              <img
                src={player.imageUrl || "/SLAM_logo.png"}
                alt={player.fullName}
                style={{ maxWidth: "180px", width: "150px" }}
                className="object-cover bg-black rounded-md"
              />
              <div>
                <span className="font-bold p-3 rounded-md themeSecondary text-xl text-white">
                  27
                </span>
                <h1 className="text-4xl mt-1 font-bold drop-shadow-sm mb-1">
                  {player.fullName}
                </h1>
                <p className="text-xl pt-2 opacity-90">
                  {player?.team}{" "}
                  {player?.teamAbbrev ? `(${player?.teamAbbrev})` : ""} ·{" "}
                  {player?.Player}
                  {/* {player.height && player.weight ? ` · ${player.height} · ${player.weight} lbs` : ''} */}
                </p>
              </div>
            </div>
          </div>

          {/* Stats Grid */}
          <div
            className="p-6 grid grid-cols-2 md:grid-cols-2 gap-2 text-sm text-[#F5F5F5] transition-colors"
          >
            <div className="rounded-md mb-2 p-2 mr-2 bg-[#111111] border border-[#1A1A1A] transition-colors">
              <strong className="text-[#E8B800]">Position:</strong> {player?.position}
            </div>
            <div className="rounded-md mb-2 p-2 mr-2 bg-[#111111] border border-[#1A1A1A] transition-colors">
              <strong className="text-[#E8B800]">B/T:</strong> -
            </div>
            <div
              className="rounded-md mb-2 p-2 mr-2 bg-[#111111] border border-[#1A1A1A] transition-colors"
            >
              <strong className="text-[#E8B800]">Height:</strong> {player?.height || "-"}
            </div>
            <div
              className="rounded-md mb-2 p-2 mr-2 bg-[#111111] border border-[#1A1A1A] transition-colors"
            >
              <strong className="text-[#E8B800]">Weight:</strong> {player?.weight || "-"}
            </div>
            <div className="rounded-md mb-2 p-2 mr-2 bg-[#111111] border border-[#1A1A1A] transition-colors">
              <strong className="text-[#E8B800]">Class:</strong> -
            </div>
            <div className="rounded-md mb-2 p-2 mr-2 bg-[#111111] border border-[#1A1A1A] transition-colors">
              <strong className="text-[#E8B800]">Hometown:</strong> -
            </div>
            <div
              className="rounded-md mb-2 p-2 mr-2 bg-[#111111] border border-[#1A1A1A] transition-colors"
            >
              <strong className="text-[#E8B800]">High School:</strong> -
            </div>
          </div>

          {/* Health/Biometric Integration */}
          {(sportType === "MLB" || sportType === "CBB") && (
            <div className="mt-4 mb-2">
              <BiometricBadgesRow
                playerId={player?.PlayerID || player?.mlbId || player?.player_id || playerId}
                playerName={player?.name || player?.Player}
              />
            </div>
          )}
        </div>

        <div className="p-8">
          <Tabs
            value={activeMainTab}
            onValueChange={(value) => setActiveMainTab(value)}
            className="mb-6"
          >
            <TabsList className="w-full justify-start gap-0 h-auto p-0 rounded-none border-b bg-transparent border-[#1A1A1A]">
              <TabsTrigger
                value="playerStats"
                className="relative px-6 py-4 rounded-none border-none data-[state=active]:shadow-none data-[state=active]:bg-transparent text-[#999999] hover:bg-[rgba(255,255,255,0.04)] data-[state=active]:text-[#E8B800] after:absolute after:bottom-0 after:left-0 after:right-0 after:h-[3px] after:bg-[#E8B800] after:rounded-t after:opacity-0 data-[state=active]:after:opacity-100"
              >
                <span className="flex items-center font-medium text-[#F5F5F5]">
                  <BarChart3 className="px-1" size={16} />
                  Stats
                </span>
              </TabsTrigger>
            </TabsList>

            <TabsContent value="playerStats" className="mt-6">
              {tableLoading ? (
                <div className="flex justify-center items-center p-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-blue-500"></div>
                  <p
                    className="ml-3 text-[#999999]"
                  >
                    Loading stats...
                  </p>
                </div>
              ) : (
                <>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                    {/* Sport-specific stats displays */}
                    {sportType === "CBB" && (
                      <>
                        {/* Baseball stats - with conditional display for pitcher/batter */}
                        {player.isPitcher ? (
                          /* Pitcher Stats */
                          <div>
                            <h2
                              className="text-2xl font-bold text-[#F5F5F5] mb-4"
                            >
                              Pitching Statistics
                            </h2>

                            <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-6">
                              <div
                                className="text-[#F5F5F5] bg-[#111111] border border-[#1A1A1A] p-4 rounded-lg text-center transition-colors"
                              >
                                <div className="text-3xl font-bold text-[#E8B800]">
                                  {player.era}
                                </div>
                                <div
                                  className="text-sm text-[#999999]"
                                >
                                  ERA
                                </div>
                              </div>
                              <div
                                className="bg-[#111111] text-[#F5F5F5] border border-[#1A1A1A] p-4 rounded-lg text-center transition-colors"
                              >
                                <div className="text-3xl font-bold text-cyan-400">
                                  {player.so || 0}
                                </div>
                                <div
                                  className="text-sm text-[#999999]"
                                >
                                  SO
                                </div>
                              </div>
                              <div
                                className="bg-[#111111] text-[#F5F5F5] border border-[#1A1A1A] p-4 rounded-lg text-center transition-colors"
                              >
                                <div className="text-3xl font-bold text-green-400">
                                  {player.ip || 0}
                                </div>
                                <div
                                  className="text-sm text-[#999999]"
                                >
                                  IP
                                </div>
                              </div>
                            </div>

                            <div
                              className="bg-[#111111] border border-[#1A1A1A] p-4 rounded-lg mb-6 transition-colors"
                            >
                              <h3
                                className="font-bold text-[#F5F5F5] mb-3"
                              >
                                Detailed Pitching Stats
                              </h3>
                              <div className="grid grid-cols-2 gap-y-3">
                                <div className="flex justify-between pr-4">
                                  <span className="text-[#999999]">Games</span>
                                  <span className="font-medium text-[#999999]">{player.games}</span>
                                </div>
                                <div className="flex justify-between pr-4">
                                  <span className="text-[#999999]">Innings Pitched</span>
                                  <span className="font-medium text-[#999999]">{player.ip || 0}</span>
                                </div>
                                <div className="flex justify-between pr-4">
                                  <span className="text-[#999999]">Batters Faced</span>
                                  <span className="font-medium text-[#999999]">{player.bf || 0}</span>
                                </div>
                                <div className="flex justify-between pr-4">
                                  <span className="text-[#999999]">Earned Runs</span>
                                  <span className="font-medium text-[#999999]">{player.er || 0}</span>
                                </div>
                                <div className="flex justify-between pr-4">
                                  <span className="text-[#999999]">Strikeouts</span>
                                  <span className="font-medium text-[#999999]">{player.so || 0}</span>
                                </div>
                                <div className="flex justify-between pr-4">
                                  <span className="text-[#999999]">Walks</span>
                                  <span className="font-medium text-[#999999]">{player.bb || 0}</span>
                                </div>
                                <div className="flex justify-between pr-4">
                                  <span className="text-[#999999]">Pitches</span>
                                  <span className="font-medium text-[#999999]">{player.pit || 0}</span>
                                </div>
                                <div className="flex justify-between pr-4">
                                  <span className="text-[#999999]">ERA</span>
                                  <span className="font-medium text-[#999999]">{player.era}</span>
                                </div>
                                <div className="flex justify-between pr-4">
                                  <span className="text-[#999999]">Wins-Losses</span>
                                  <span className="font-medium text-[#999999]">{player.wins || 0}-{player.losses || 0}</span>
                                </div>
                                <div className="flex justify-between pr-4">
                                  <span className="text-[#999999]">Saves</span>
                                  <span className="font-medium text-[#999999]">{player.saves || 0}</span>
                                </div>
                              </div>
                            </div>
                          </div>
                        ) : (
                          /* Batter Stats */
                          <div>
                            <h2
                              className="text-2xl font-bold text-[#F5F5F5] mb-4"
                            >
                              Batting Statistics
                            </h2>

                            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                              <div
                                className="bg-[#111111] border border-[#1A1A1A] p-4 rounded-lg text-center transition-colors"
                              >
                                <div className="text-3xl font-bold text-[#E8B800]">
                                  {player.battingAverage}
                                </div>
                                <div className="text-sm text-[#999999]">
                                  AVG
                                </div>
                              </div>
                              <div
                                className="bg-[#111111] border border-[#1A1A1A] p-4 rounded-lg text-center transition-colors"
                              >
                                <div className="text-3xl font-bold text-cyan-400">
                                  {player.obp}
                                </div>
                                <div className="text-sm text-[#999999]">
                                  OBP
                                </div>
                              </div>
                              <div
                                className="bg-[#111111] border border-[#1A1A1A] p-4 rounded-lg text-center transition-colors"
                              >
                                <div className="text-3xl font-bold text-green-400">
                                  {player.slg}
                                </div>
                                <div className="text-sm text-[#999999]">
                                  SLG
                                </div>
                              </div>
                              <div
                                className="bg-[#111111] border border-[#1A1A1A] p-4 rounded-lg text-center transition-colors"
                              >
                                <div className="text-3xl font-bold text-purple-400">
                                  {player.ops}
                                </div>
                                <div className="text-sm text-[#999999]">
                                  OPS
                                </div>
                              </div>
                            </div>

                            <div
                              className="bg-[#111111] border border-[#1A1A1A] p-4 rounded-lg mb-6 transition-colors"
                            >
                              <h3
                                className="font-bold text-[#F5F5F5] mb-3"
                              >
                                Hitting Stats
                              </h3>
                              <div className="grid grid-cols-2 gap-y-2">
                                <div className="flex justify-between pr-4">
                                  <span className="text-[#999999]">Games</span>
                                  <span className="font-medium text-[#999999]">{player.games}</span>
                                </div>
                                <div className="flex justify-between pr-4">
                                  <span className="text-[#999999]">At Bats</span>
                                  <span className="font-medium text-[#999999]">{player.atBats}</span>
                                </div>
                                <div className="flex justify-between pr-4">
                                  <span className="text-[#999999]">Runs</span>
                                  <span className="font-medium text-[#999999]">{player.runs}</span>
                                </div>
                                <div className="flex justify-between pr-4">
                                  <span className="text-[#999999]">Hits</span>
                                  <span className="font-medium text-[#999999]">{player.hits}</span>
                                </div>
                                <div className="flex justify-between pr-4">
                                  <span className="text-[#999999]">HR</span>
                                  <span className="font-medium text-[#999999]">{player.homeRuns}</span>
                                </div>
                                <div className="flex justify-between pr-4">
                                  <span className="text-[#999999]">RBI</span>
                                  <span className="font-medium text-[#999999]">{player.rbi}</span>
                                </div>
                              </div>
                            </div>
                          </div>
                        )}
                      </>
                    )}
                    {sportType === "CBB" && (
                      <div>
                        <h2
                          className="text-2xl font-bold text-[#F5F5F5] mb-4"
                        >
                          Athletic Metrics
                        </h2>
                        <div className="space-y-4 mb-6">
                          <div>
                            <div className="flex justify-between mb-1">
                              <span
                                className="font-medium text-[#F5F5F5]"
                              >
                                Exit Velocity
                              </span>
                              <span className="text-green-700 font-bold">
                                {player.exitVelo} MPH
                              </span>
                            </div>
                            <div
                              className="w-full bg-[#1A1A1A] rounded-full h-2 transition-colors"
                            >
                              <div
                                className="bg-green-600 h-2 rounded-full"
                                style={{
                                  width: `${Math.min(
                                    100,
                                    (player.exitVelo / 105) * 100
                                  )}%`,
                                }}
                              ></div>
                            </div>
                          </div>
                          <div>
                            <div className="flex justify-between mb-1">
                              <span
                                className="font-medium text-[#F5F5F5]"
                              >
                                Swing/Miss Rate
                              </span>
                              <span className="text-green-700 font-bold">
                                {player.swingMiss}%
                              </span>
                            </div>
                            <div
                              className="w-full bg-[#1A1A1A] rounded-full h-2 transition-colors"
                            >
                              <div
                                className="bg-green-600 h-2 rounded-full"
                                style={{ width: `${player.swingMiss}%` }}
                              ></div>
                            </div>
                          </div>
                          <div>
                            <div className="flex justify-between mb-1">
                              <span
                                className="font-medium text-[#F5F5F5]"
                              >
                                Speed
                              </span>
                              <span className="text-green-700 font-bold">
                                {player.speed} ft/s
                              </span>
                            </div>
                            <div
                              className="w-full bg-[#1A1A1A] rounded-full h-2 transition-colors"
                            >
                              <div
                                className="bg-green-600 h-2 rounded-full"
                                style={{
                                  width: `${Math.min(
                                    100,
                                    (player.speed / 35) * 100
                                  )}%`,
                                }}
                              ></div>
                            </div>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                  <CareerStatsComponent
                    seasonStats={seasonStats}
                    sportType={sportType}
                    player={player}
                  />
                  <GameStatsTab
                    getPlayerStats={(page, size, type) => {
                      fetchTeamStatsData(
                        page || currentPage,
                        size || pageSize,
                        type
                      );
                    }}
                    playersData={tableStats}
                    activeStatsTab={activeStatsTab}
                    setActiveStatsTab={setActiveStatsTab}
                    isLoading={tableLoading}
                  />
                  <div className="mt-6 flex justify-center pb-5">
                    <Pagination
                      current={currentPage}
                      pageSize={pageSize}
                      total={totalTeamsStats}
                      onChange={handlePageChange}
                      showSizeChanger
                      pageSizeOptions={[5, 10, 20, 50]}
                      isDarkMode={true}
                    />
                  </div>
                </>
              )}
            </TabsContent>

            {/* Note: To enable the Media tab, also add this TabsTrigger to the TabsList above:
              <TabsTrigger
                value="playerDevelopment"
                className="relative px-6 py-4 rounded-none border-none data-[state=active]:shadow-none data-[state=active]:bg-transparent text-[#999999] hover:bg-[rgba(255,255,255,0.04)] data-[state=active]:text-[#E8B800] after:absolute after:bottom-0 after:left-0 after:right-0 after:h-[3px] after:bg-[#E8B800] after:rounded-t after:opacity-0 data-[state=active]:after:opacity-100"
              >
                <span className="flex items-center font-medium text-[#999999]">
                  <Image className="px-1" size={16} />
                  Media
                </span>
              </TabsTrigger>
            */}
            {/* <TabsContent value="playerDevelopment" className="mt-6">
              <div className={sportType === 'CBB' ? "" : "md:col-span-2"}>
                {!selectedFolderId ? (
                  <div className="pb-4">
                    <div className="border rounded-lg bg-[#111111] border-[#1A1A1A]">
                      <div className="p-4 bg-[#111111] border-[#1A1A1A] border-b flex justify-between items-center">
                        <div>
                          <h3 className="text-xl font-semibold text-blue-700">Folder Management</h3>
                          <p className="text-sm text-blue-600 mt-1">{allFolders.length || 0} folders found</p>
                        </div>
                        <button
                          onClick={() => fetchAllFolders()}
                          className="flex items-center px-3 py-2 text-sm font-medium rounded-lg bg-blue-300 hover:bg-blue-400 text-blue-700 transition-colors"
                        >
                          <RefreshCw size={16} className="mr-2" />
                          Refresh
                        </button>
                      </div>

                      <div className="overflow-x-auto">
                        <table className="w-full bg-[#111111]">
                          <thead className="bg-[#0A0A0A] text-[#999999]">
                            <tr>
                              <th className="text-left p-4 font-medium text-sm">FOLDER NAME</th>
                              <th className="text-left p-4 font-medium text-sm">CREATED AT</th>
                              <th className="text-left p-4 font-medium text-sm">ACTIONS</th>
                            </tr>
                          </thead>
                          <tbody>
                            {isFolderLoading ? (
                              <tr>
                                <td colSpan="3" className="text-center py-12">
                                  <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500 mx-auto mb-4"></div>
                                  <p className="text-[#999999]">Loading folders...</p>
                                </td>
                              </tr>
                            ) : allFolders.length === 0 ? (
                              <tr>
                                <td colSpan="3" className="text-center py-12 text-gray-500">
                                  <Folder className="mx-auto w-12 h-12 mb-4 text-gray-400" />
                                  <p>No folders found</p>
                                  <p className="text-sm">Create a folder to organize your videos</p>
                                </td>
                              </tr>
                            ) : (
                              allFolders.map((folder) => (
                                <tr
                                  key={folder.id}
                                  className="border-b border-[#1A1A1A] hover:bg-[rgba(255,255,255,0.04)] transition-colors cursor-pointer"
                                  onClick={() => handleFolderClick(folder.id, folder.name)}
                                >
                                  <td className="p-4">
                                    <div className="flex items-center">
                                      <Folder className={`w-5 h-5 mr-2 ${selectedFolderId === folder.id ? "text-[#E8B800]" : "text-[#999999]"}`} />
                                      <span className={`font-medium ${selectedFolderId === folder.id ? "text-[#E8B800]" : "text-[#F5F5F5]"}`}>
                                        {folder.name}
                                      </span>
                                    </div>
                                  </td>
                                  <td className="p-4">
                                    <div className="flex items-center">
                                      <Calendar className="w-4 h-4 mr-2 text-[#999999]" />
                                      <span className="text-[#F5F5F5]">
                                        {new Date(folder.created_at).toLocaleDateString()}
                                      </span>
                                      <span className="text-xs text-gray-500 ml-2">
                                        {new Date(folder.created_at).toLocaleTimeString()}
                                      </span>
                                    </div>
                                  </td>
                                  <td className="p-4">
                                    <div className="flex space-x-2">
                                      <button
                                        className="flex items-center px-3 py-1 text-xs font-medium rounded bg-blue-100 text-blue-700 hover:bg-blue-200 transition-colors"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          handleFolderClick(folder.id, folder.name);
                                        }}
                                      >
                                        <ExternalLink size={12} className="mr-1" />
                                        Open
                                      </button>
                                      <ConfirmDialog
                                        title="Delete Folder"
                                        description="Are you sure you want to delete this folder?"
                                        onConfirm={(e) => {
                                          e?.stopPropagation?.();
                                          handleDeleteFolder(folder.id);
                                        }}
                                        okText="Yes"
                                        cancelText="No"
                                        onCancel={(e) => e?.stopPropagation?.()}
                                        destructive
                                      >
                                        <button
                                          className="flex items-center px-3 py-1 text-xs font-medium rounded bg-red-100 text-red-700 hover:bg-red-200 transition-colors"
                                          onClick={(e) => e.stopPropagation()}
                                        >
                                          <Trash2 size={12} className="mr-1" />
                                          Delete
                                        </button>
                                      </ConfirmDialog>
                                    </div>
                                  </td>
                                </tr>
                              ))
                            )}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div>
                    <div className="px-4 mb-4">
                      <button
                        onClick={() => {
                          setSelectedFolderId(null);
                          navigate(`/player/${playerId}`);
                          setExistingVideos([]);
                        }}
                        className="px-4 py-2 rounded-lg flex items-center bg-[#E8B800] text-black hover:bg-[#d4a800]"
                      >
                        <ChevronLeft size={18} className="mr-2" />
                        Back to Folders
                      </button>
                    </div>
                    <VideoAnalyticsTable 
                      onRefresh={fetchExistingVideos} 
                      handlePageChange={handleVideoPageChange} 
                      setCurrentPage={setCurrentVideoPage} 
                      is_player_screen={true} 
                      currentPage={currentVideoPage} 
                      pageSize={videoPageSize} 
                      setPageSize={setVideoPageSize} 
                      totalRecords={totalVideoRecords} 
                      existingVideos={existingVideos} 
                      setExistingVideos={setExistingVideos}
                      selectedFolderId={selectedFolderId}
                      allFolders={allFolders}
                      isLoading={isTableLoading}
                    />
                  </div>
                )}
              </div>
              {player.highlights && player.highlights.length > 0 && (
                <div className="mt-8">
                  <h2
                    className="text-2xl font-bold text-[#F5F5F5] mb-4"
                  >
                    Highlights
                  </h2>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {player.highlights.map((highlight, index) => (
                      <div
                        key={index}
                        className="rounded-[14px] shadow overflow-hidden transition-colors bg-[#0A0A0A] border border-[#1A1A1A]"
                      >
                        <div className="aspect-w-16 aspect-h-9">
                          <iframe
                            src={highlight.url}
                            frameBorder="0"
                            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                            allowFullScreen
                            title={highlight.title || `Highlight ${index + 1}`}
                            className="w-full h-full"
                          ></iframe>
                        </div>
                        <div className="p-4">
                          <h3
                            className="font-bold text-[#F5F5F5]"
                          >
                            {highlight.title || `Highlight ${index + 1}`}
                          </h3>
                          {highlight.description && (
                            <p
                              className="text-[#999999] text-sm mt-1"
                            >
                              {highlight.description}
                            </p>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </TabsContent> */}
          </Tabs>
        </div>
      </div>

      <div className="mt-8 text-center">
        <button
          onClick={() => navigate(-1)}
          className="px-6 py-3 themeSecondary text-white rounded-md hover:themeSecondary transition-colors"
        >
          Back to Roster
        </button>
      </div>

      {videoModal && (
        <VideoAnalyticsModal
          fetchExistingVideos={() => fetchExistingVideos()}
          onClose={() => setVideoModal(false)}
          playerId={player.id}
          isOpen={videoModal}
        />
      )}
    </div>
  );
};

export default TeamPlayerProfile;
